// Readers for the widget blob (JSON:API items, a bare array or a `{ tournaments }` envelope).

import { intlLocale } from '../../group_leader/helpers/i18n'

const withoutDuplicates = (entries) => {
  const seen = new Set()

  return entries.filter((entry) => {
    // id_str first: a real aggregate id exceeds JS's safe-integer range, so `id`s can collide.
    const key = entry.id_str ?? entry.id ?? entry.player_id ?? entry.member_id
    // An entry with no identifier of any kind cannot be de-duplicated; keep it.
    if (key === null || key === undefined) { return true }
    if (seen.has(key)) { return false }
    seen.add(key)
    return true
  })
}

// Every aggregate across the scopes, once each.
const scopeAggregates = (leaderboard) =>
  withoutDuplicates((leaderboard?.event?.scopes || []).flatMap((scope) => scope.aggregates || []))

export const normaliseItem = (item) =>
  (item?.attributes ? { id: item.id, ...item.attributes } : item)

export const extractTournaments = (widgetData) => {
  if (!widgetData) { return [] }

  const items = Array.isArray(widgetData)
    ? widgetData
    : (widgetData.tournaments || widgetData.data || [])

  return Array.isArray(items) ? items.map(normaliseItem) : []
}

export const extractLeaderboardEntries = (tournament) => {
  const leaderboard = tournament.leaderboard

  if (leaderboard?.event?.scopes) { return scopeAggregates(leaderboard) }
  if (Array.isArray(leaderboard)) { return leaderboard.map(normaliseItem) }
  if (Array.isArray(leaderboard?.entries)) { return leaderboard.entries }
  return []
}

// Blank or no digit but zeros: "$0.00", "0", "-" (not scored yet), "".
const isZero = (value) => !parseFloat(String(value ?? '').replace(/[^.0-9]/g, ''))

// A column the game sends, with nothing in it.
const isEmpty = (value) => value !== undefined && isZero(value)

// No purse and no skins (`total`) or instances (`score`): left out, as _result_scope.html.haml does.
const isEmptyRow = (entry) => isZero(entry.purse) && (isEmpty(entry.total) || isEmpty(entry.score))

export const withoutEmptyRows = (entries) => entries.filter((entry) => !isEmptyRow(entry))

// Internal names ("individual_stroke_play") are title-cased; a spaced display name is kept.
export const gameDisplayName = (name) => {
  if (!name) { return '' }
  if (!name.includes('_')) { return name }
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export const entryToPar = (entry) => entry.score ?? entry.to_par ?? null
export const entryGross = (entry) => entry.total ?? entry.gross ?? null

// get_foursome_status's only in-progress status, per foursome: the group's card is under way.
export const SCORECARD_STARTED = 'partial'

export const entryIsPlaying = (entry) =>
  (entry.scorecard_statuses || []).some((card) => card.status === SCORECARD_STARTED)

// Head-to-head has two aggregates per scope; "player v. all others" one per player across scopes.
export const isMatchPlay = (tournament) => {
  const scoreColumn = tournament.leaderboard?.event?.column_names?.score || ''
  return scoreColumn === 'Match' || scoreColumn.startsWith('Matches')
}

export const isMultiMatchPlay = (leaderboard) =>
  (leaderboard?.event?.column_names?.score || '').startsWith('Matches')

// The legacy order, ascending id as strings (tournaments2/events/match/_result_scope.html.haml:2).
const bySideOrder = (side, other) =>
  String(side.id_str || side.id).localeCompare(String(other.id_str || other.id))

// "T1" on a tie, "1"/"2" otherwise (NewAggregate#compute_match_rankings).
export const rankOf = (position) => parseInt(String(position ?? '').replace(/^T/, ''), 10)

export const MATCH_TBD = 'tbd'

// Equal ranks means halved; blobs with no `position` use the blanked loser score (scoring/engine.rb:3908).
const decideWinner = (first, second) => {
  const firstRank = rankOf(first?.position)
  const secondRank = rankOf(second?.position)
  if (!Number.isNaN(firstRank) && !Number.isNaN(secondRank)) {
    if (firstRank === secondRank) { return null }
    return firstRank < secondRank ? first : second
  }

  const firstScore = (first?.score || '').trim()
  const secondScore = (second?.score || '').trim()
  if (firstScore && !secondScore) { return first }
  if (secondScore && !firstScore) { return second }
  return null
}

// One row per scope, with the winner (null when halved); a bracket bye is kept, as the legacy page does.
export const extractMatches = (leaderboard) =>
  (leaderboard?.event?.scopes || []).flatMap((scope) => {
    const sides = (scope.aggregates || []).slice().sort(bySideOrder)
    if (sides.length === 0) { return [] }
    if (sides.length === 1) {
      return [ { first: sides[0], second: null, winner: sides[0], result: sides[0].score || '', tbd: false } ]
    }

    const [ first, second ] = sides
    const tbd = first.score === MATCH_TBD || second.score === MATCH_TBD
    const winner = tbd ? null : decideWinner(first, second)
    return [ { first, second, winner, result: tbd ? '' : ((winner || first).score || ''), tbd } ]
  })

export const extractMultiMatchEntries = scopeAggregates

// The account decides 12 vs 24 hour and the app locale the meridiem, as the server's time_formats do.
export const formatClockTime = (date, hourFormat) => {
  if (!date) { return null }

  // intlLocale, not window.I18n.locale: Intl rejects 'fr_fr' outright and blanks the page.
  return date.toLocaleTimeString(intlLocale(), {
    hour: 'numeric', minute: '2-digit', hour12: hourFormat === 'am_pm',
  })
}

// The customer's date pattern, from the string's parts: `new Date('2026-08-30')` is UTC midnight.
export const formatEventDate = (isoDate, pattern) => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate || '')
  if (!parts) { return isoDate || '' }

  const [ , year, month, day ] = parts
  return (pattern || 'yyyy-mm-dd')
    .replace('yyyy', year)
    .replace('mm', month)
    .replace('dd', day)
}
